/**
 * Search Mega Menu Web Component
 * Handles both desktop mega menu and mobile drawer search interfaces
 */
class SearchMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.isMobile = window.innerWidth < 750;

    // Search state
    this.currentSearchTerm = '';
    this.searchCache = {};
    this.abortController = null;
  }

  connectedCallback() {
    // Initialize DOM references
    this.desktop = this.querySelector('.search-mega-menu__desktop');
    this.mobile = this.querySelector('.search-mega-menu__mobile');
    this.closeButtons = this.querySelectorAll('.search-mega-menu__close, .search-mega-menu__mobile-close');
    this.predictiveSearchInstances = this.querySelectorAll('predictive-search');

    // Search inputs and placeholder content
    this.searchInputs = this.querySelectorAll('#search-mega-menu-desktop, #search-mega-menu-mobile');
    this.placeholderContents = this.querySelectorAll('[data-search-placeholder]');

    this.setupEventListeners();
    this.handleResize();
    this.initializePlaceholderState();
    this.setupSearchTriggers();
  }

  setupEventListeners() {
    // Close buttons
    this.closeButtons.forEach(button => {
      button.addEventListener('click', this.close.bind(this));
    });

    // Escape key to close
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Click outside to close (desktop only)
    document.addEventListener('click', (event) => {
      if (!this.isMobile && this.isOpen && !this.contains(event.target)) {
        this.close();
      }
    });

    // Handle window resize
    window.addEventListener('resize', this.handleResize.bind(this));

    // Prevent clicks inside from bubbling up
    this.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    // Search input event listeners with debounced search
    this.searchInputs.forEach(input => {
      input.addEventListener('input', this.debounce(this.handleSearchInput.bind(this), 300));
      input.addEventListener('focus', this.handleSearchFocus.bind(this));
    });
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 750;

    // If switching between mobile/desktop, close the menu
    if (wasMobile !== this.isMobile && this.isOpen) {
      this.close();
    }
  }

  open(isFixedHeader = false) {
    if (this.isOpen) return;

    this.isOpen = true;
    this.setAttribute('open', '');

    // Handle fixed header positioning for desktop
    if (!this.isMobile && isFixedHeader) {
      this.setAttribute('data-fixed-header', 'true');
      this.adjustFixedHeaderPositioning();
    } else {
      this.removeAttribute('data-fixed-header');
    }

    if (this.isMobile) {
      document.body.classList.add('overflow-hidden');
    }

    // Focus on search input
    const searchInput = this.isMobile
      ? this.querySelector('#search-mega-menu-mobile')
      : this.querySelector('#search-mega-menu-desktop');

    if (searchInput) {
      // Small delay to ensure the element is visible
      setTimeout(() => {
        searchInput.focus();
      }, 100);
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('search-mega-menu:opened'));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.removeAttribute('open');

    // Reset fixed header positioning if it was used
    if (this.hasAttribute('data-fixed-header')) {
      this.removeAttribute('data-fixed-header');
      this.resetDesktopMenuPositioning();
    }

    if (this.isMobile) {
      // Wait for animation before removing body overflow
      setTimeout(() => {
        document.body.classList.remove('overflow-hidden');
      }, 300);
    }

    // Clear search inputs
    this.clearSearchInputs();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('search-mega-menu:closed'));
  }

  adjustFixedHeaderPositioning() {
    // Find the fixed header element
    const fixedHeader = document.querySelector('.header--on-scroll:not(.hidden)');
    if (!fixedHeader) return;

    const desktopMenu = this.querySelector('.search-mega-menu__desktop');
    if (!desktopMenu) return;

    // Get fixed header dimensions and position
    const headerRect = fixedHeader.getBoundingClientRect();
    const headerHeight = headerRect.height;

    // Position the menu directly below the fixed header
    desktopMenu.style.position = 'fixed';
    desktopMenu.style.top = `${headerHeight}px`;
    desktopMenu.style.left = '0';
    desktopMenu.style.transform = 'translateX(0)';
    desktopMenu.style.width = '100vw';
    desktopMenu.style.zIndex = '999';
  }

  resetDesktopMenuPositioning() {
    const desktopMenu = this.querySelector('.search-mega-menu__desktop');
    if (!desktopMenu) return;

    // Reset to original CSS positioning
    desktopMenu.style.position = '';
    desktopMenu.style.top = '';
    desktopMenu.style.left = '';
    desktopMenu.style.transform = '';
    desktopMenu.style.width = '';
    desktopMenu.style.zIndex = '';
  }

  clearSearchInputs() {
    const inputs = this.querySelectorAll('.search-mega-menu__input');
    inputs.forEach(input => {
      input.value = '';
      input.setAttribute('aria-expanded', 'false');

      // Hide reset button
      this.updateResetButtonVisibility(input, '');
    });

    // Clear current search term and abort requests
    this.currentSearchTerm = '';
    if (this.abortController) {
      this.abortController.abort();
    }

    // Restore placeholder content when clearing inputs
    this.showPlaceholderContent();
  }

  // ===== PLACEHOLDER CONTENT METHODS =====

  initializePlaceholderState() {
    // Store original placeholder content for restoration
    this.originalPlaceholderContent = {};
    this.placeholderContents.forEach(placeholder => {
      const type = placeholder.getAttribute('data-search-placeholder');
      this.originalPlaceholderContent[type] = placeholder.innerHTML;
    });

    // Show placeholder content by default when menu opens
    this.showPlaceholderContent();

    // Set up reset button listeners
    this.setupResetButtonListeners();
  }

  setupResetButtonListeners() {
    const resetButtons = this.querySelectorAll('.search-mega-menu__reset');
    resetButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const input = button.parentNode.querySelector('.search-mega-menu__input');
        if (input) {
          input.value = '';
          this.currentSearchTerm = '';

          // Determine type and restore placeholder content
          const isDesktop = input.id === 'search-mega-menu-desktop';
          const type = isDesktop ? 'desktop' : 'mobile';

          this.restoreOriginalPlaceholderContent(type);
          button.classList.add('hidden');

          // Abort any pending requests
          if (this.abortController) {
            this.abortController.abort();
          }
        }
      });
    });
  }

  restoreOriginalPlaceholderContent(type) {
    const placeholderContainer = this.querySelector(`[data-search-placeholder="${type}"]`);
    if (placeholderContainer && this.originalPlaceholderContent[type]) {
      placeholderContainer.innerHTML = this.originalPlaceholderContent[type];
      placeholderContainer.style.display = 'block';
      placeholderContainer.classList.remove('hidden');
    }
  }

  showPlaceholderContent(type = 'both') {
    // Restore original placeholder content if it was replaced by search results
    if (type === 'both') {
      ['desktop', 'mobile'].forEach(t => {
        this.restoreOriginalPlaceholderContent(t);
      });
    } else {
      this.restoreOriginalPlaceholderContent(type);
    }

    this.placeholderContents.forEach(placeholder => {
      const placeholderType = placeholder.getAttribute('data-search-placeholder');

      if (type === 'both' || type === placeholderType) {
        placeholder.style.display = 'block';
        placeholder.classList.remove('hidden');
      }
    });
  }

  hidePlaceholderContent(type = 'both') {
    this.placeholderContents.forEach(placeholder => {
      const placeholderType = placeholder.getAttribute('data-search-placeholder');

      if (type === 'both' || type === placeholderType) {
        placeholder.style.display = 'none';
        placeholder.classList.add('hidden');
      }
    });
  }

  // Debounce utility function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  async handleSearchInput(event) {
    const input = event.target;
    const inputValue = input.value.trim();
    const isDesktop = input.id === 'search-mega-menu-desktop';
    const type = isDesktop ? 'desktop' : 'mobile';

    // Update reset button visibility
    this.updateResetButtonVisibility(input, inputValue);

    if (inputValue === '') {
      // Show placeholder content when input is empty
      this.showPlaceholderContent(type);
      this.hideSearchResults(type);
      this.currentSearchTerm = '';

      // Abort any pending requests
      if (this.abortController) {
        this.abortController.abort();
      }
    } else if (inputValue !== this.currentSearchTerm) {
      // Perform search when input has content and is different from current term
      this.currentSearchTerm = inputValue;
      await this.performSearch(inputValue, type);
    }
  }

  handleSearchFocus(event) {
    const input = event.target;
    const inputValue = input.value.trim();
    const isDesktop = input.id === 'search-mega-menu-desktop';
    const type = isDesktop ? 'desktop' : 'mobile';

    // If input is empty on focus, ensure placeholder content is visible
    if (inputValue === '') {
      this.showPlaceholderContent(type);
    }
  }

  updateResetButtonVisibility(input, inputValue) {
    const resetButton = input.parentNode.querySelector('.search-mega-menu__reset');
    if (resetButton) {
      if (inputValue.length > 0) {
        resetButton.classList.remove('hidden');
      } else {
        resetButton.classList.add('hidden');
      }
    }
  }

  // ===== SEARCH API METHODS =====

  async performSearch(searchTerm, type) {
    const deviceType = type === 'desktop' ? 'desktop' : 'mobile';
    const cacheKey = `${searchTerm}-${deviceType}`;

    try {
      // Hide placeholder content and show search results
      this.hidePlaceholderContent(type);

      // Check cache first with device type specificity
      if (this.searchCache[cacheKey]) {
        this.renderSearchResults(this.searchCache[cacheKey], type);
        return;
      }

      // Abort previous request
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      // Make API call to HTML endpoint
      const apiUrl = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search-results`;
      const response = await fetch(apiUrl, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const htmlText = await response.text();

      // Parse HTML to extract content
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      // Select the appropriate device format based on type
      const searchResultsContent = doc.querySelector(`#predictive-search-results [data-device-type="${deviceType}"]`);

      if (searchResultsContent) {
        const htmlContent = searchResultsContent.outerHTML;

        // Cache the results with device type specificity
        this.searchCache[cacheKey] = htmlContent;

        // Render the results
        this.renderSearchResults(htmlContent, type);
      } else {
        // Fallback if content structure is not found
        console.warn(`No search results content found for device type: ${deviceType}`);
        this.showPlaceholderContent(type);
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Search request aborted');
        return;
      }
      console.error('Search error:', error);
      // Show placeholder content on error
      this.showPlaceholderContent(type);
    }
  }

  renderSearchResults(htmlContent, type) {
    // Get the appropriate placeholder container to replace content
    const placeholderContainer = this.querySelector(`[data-search-placeholder="${type}"]`);
    if (!placeholderContainer) return;

    // Replace placeholder content with search results HTML from server
    placeholderContainer.innerHTML = htmlContent;
    placeholderContainer.style.display = 'block';
    placeholderContainer.classList.remove('hidden');
  }

  hideSearchResults(type) {
    // This method isn't needed anymore since we reuse placeholder containers
    // Just ensure placeholder content is shown
    this.showPlaceholderContent(type);
  }

  // Setup search triggers (header search icons)
  setupSearchTriggers() {
    const searchTriggers = document.querySelectorAll('[data-search-mega-menu-trigger]');

    if (searchTriggers.length > 0) {
      searchTriggers.forEach(trigger => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          // Detect if click came from fixed header
          const headerParent = trigger.closest('.header');
          const isFixedHeader = headerParent && headerParent.classList.contains('header--on-scroll');

          this.toggle(isFixedHeader);
        });
      });
    }
  }

  // Public API method for external triggers (like header search icon)
  toggle(isFixedHeader = false) {
    if (this.isOpen) {
      this.close();
    } else {
      this.open(isFixedHeader);
    }
  }
}

// Register custom elements
customElements.define('search-mega-menu', SearchMegaMenu);
